import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Train, RefreshCw, Loader2, AlertTriangle, Target, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { tileUrl, tileAttribution, TILE_MAX_ZOOM } from "@/lib/mapTiles";
import { useTheme } from "@/contexts/ThemeContext";

const ROUTE_COLORS = {
  "1": "#EE352E", "2": "#EE352E", "3": "#EE352E",
  "4": "#00933C", "5": "#00933C", "6": "#00933C",
  "7": "#B933AD",
  "A": "#0039A6", "C": "#0039A6", "E": "#0039A6",
  "B": "#FF6319", "D": "#FF6319", "F": "#FF6319", "M": "#FF6319",
  "G": "#6CBE45",
  "J": "#996633", "Z": "#996633",
  "L": "#A7A9AC",
  "N": "#FCCC0A", "Q": "#FCCC0A", "R": "#FCCC0A", "W": "#FCCC0A",
  "S": "#808183",
  "SI": "#053159", "SIR": "#053159",
  "H": "#808183", "FS": "#808183", "GS": "#808183",
};

function routeColor(r) { return ROUTE_COLORS[r] || "#888"; }

function trainIcon(route, direction, selected = false) {
  const bg = routeColor(route);
  const textColor = ["N", "Q", "R", "W", "FS", "GS"].includes(route) ? "#000" : "#fff";
  const ring = selected ? "#facc15" : "rgba(0,0,0,0.6)";
  const arrow = direction === "N" ? "▲" : direction === "S" ? "▼" : "●";
  return L.divIcon({
    html: `<div style="position:relative; width:30px; height:30px; display:flex; align-items:center; justify-content:center;">
      <div style="position:absolute; top:-8px; right:-4px; font-size:9px; color:${bg}; text-shadow: 0 0 3px black, 0 0 3px black;">${arrow}</div>
      <div style="width:26px; height:26px; border-radius:50%; background:${bg}; color:${textColor}; border:2px solid ${ring}; box-shadow:0 0 6px rgba(0,0,0,0.6); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:13px; font-family:'JetBrains Mono', monospace;">${route || "?"}</div>
    </div>`,
    className: "",
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

function RouteBullet({ route }) {
  const bg = routeColor(route);
  const textColor = ["N", "Q", "R", "W"].includes(route) ? "#000" : "#fff";
  return (
    <span
      className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mono shrink-0"
      style={{ background: bg, color: textColor }}
    >
      {route}
    </span>
  );
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.5 });
  }, [center, zoom, map]);
  return null;
}

function FollowPan({ position, enabled }) {
  const map = useMap();
  useEffect(() => {
    if (enabled && position) map.panTo(position, { animate: true, duration: 0.4 });
  }, [position, enabled, map]);
  return null;
}

export default function NYCTPage() {
  const { theme } = useTheme();
  const [trains, setTrains] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [center, setCenter] = useState([40.7549, -73.987]);
  const [fetched, setFetched] = useState(null);
  const [lines, setLines] = useState(null);
  const [linesVisible, setLinesVisible] = useState(true);
  const [follow, setFollow] = useState(false);
  const [trails, setTrails] = useState({}); // trip_id -> [[lat,lon], ...]
  const [alertsByRoute, setAlertsByRoute] = useState({});
  const [allAlerts, setAllAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now()); // ticks for smooth interpolation
  const timerRef = useRef(null);
  const animRef = useRef(null);
  const displayPosRef = useRef({}); // trip_id -> [lat,lon] last rendered, for jump smoothing

  // Compute live interpolated position for a train along its actual track path
  const interpolate = (t, nowSec) => {
    if (t.prev_ts == null || t.next_ts == null || t.next_ts <= t.prev_ts) {
      return [t.lat, t.lon];
    }
    const frac = Math.max(0, Math.min(1, (nowSec - t.prev_ts) / (t.next_ts - t.prev_ts)));
    const path = t.path;
    if (!path || path.length < 2) {
      // Straight-line fallback
      return [
        t.prev_lat + (t.next_lat - t.prev_lat) * frac,
        t.prev_lon + (t.next_lon - t.prev_lon) * frac,
      ];
    }
    // Compute cumulative arc length along the path
    let total = 0;
    const segLens = [];
    for (let i = 1; i < path.length; i++) {
      const dy = path[i][0] - path[i - 1][0];
      const dx = path[i][1] - path[i - 1][1];
      const d = Math.sqrt(dy * dy + dx * dx);
      segLens.push(d);
      total += d;
    }
    if (total === 0) return path[0];
    let target = total * frac;
    for (let i = 0; i < segLens.length; i++) {
      if (target <= segLens[i]) {
        const f = segLens[i] === 0 ? 0 : target / segLens[i];
        return [
          path[i][0] + (path[i + 1][0] - path[i][0]) * f,
          path[i][1] + (path[i + 1][1] - path[i][1]) * f,
        ];
      }
      target -= segLens[i];
    }
    return path[path.length - 1];
  };

  // Animation tick (4 Hz) — re-renders markers at smoothly-interpolated positions
  useEffect(() => {
    animRef.current = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(animRef.current);
  }, []);

  // Decorate trains with live interpolated lat/lon based on current tick
  const liveTrains = useMemo(() => {
    const nowSec = nowMs / 1000;
    return trains.map((t) => {
      const [lat, lon] = interpolate(t, nowSec);
      return { ...t, lat, lon };
    });
  }, [trains, nowMs]);

  const liveSelected = useMemo(() => {
    if (!selected) return null;
    return liveTrains.find((t) => t.trip_id === selected.trip_id) || selected;
  }, [liveTrains, selected]);

  const fetchTrains = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/nyct/trains");
      const list = data.trains || [];
      setTrains(list);
      setFetched(data.fetched_at);
      // Update trails (keep last 8 points per trip)
      setTrails((prev) => {
        const next = { ...prev };
        const liveIds = new Set();
        list.forEach((t) => {
          liveIds.add(t.trip_id);
          const arr = next[t.trip_id] ? [...next[t.trip_id]] : [];
          const last = arr[arr.length - 1];
          if (!last || last[0] !== t.lat || last[1] !== t.lon) {
            arr.push([t.lat, t.lon]);
            if (arr.length > 8) arr.shift();
          }
          next[t.trip_id] = arr;
        });
        // Drop trails for finished trips
        Object.keys(next).forEach((id) => { if (!liveIds.has(id)) delete next[id]; });
        return next;
      });
      if (selected) {
        const found = list.find((t) => t.trip_id === selected.trip_id);
        if (found) setSelected(found);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const fetchAlerts = async () => {
    try {
      const { data } = await api.get("/nyct/alerts");
      const list = data.alerts || [];
      setAllAlerts(list);
      const map = {};
      list.forEach((a) => a.routes.forEach((r) => {
        map[r] = (map[r] || []).concat(a);
      }));
      setAlertsByRoute(map);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchTrains();
    api.get("/nyct/lines").then((r) => setLines(r.data)).catch(() => {});
    fetchAlerts();
    timerRef.current = setInterval(() => {
      fetchTrains();
      fetchAlerts();
    }, 15000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const routeCounts = useMemo(() => {
    const c = {};
    trains.forEach((t) => { const r = t.route || "?"; c[r] = (c[r] || 0) + 1; });
    return c;
  }, [trains]);
  const routes = useMemo(() => Object.keys(routeCounts).sort(), [routeCounts]);

  const selectTrain = (t) => {
    setSelected(t);
    setCenter([t.lat, t.lon]);
  };

  const selectedAlerts = selected ? (alertsByRoute[selected.route] || []) : [];

  const linesStyle = (feature) => ({
    color: routeColor(feature.properties?.service),
    weight: 3,
    opacity: 0.55,
  });

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="nyct-page">
      {/* Top status bar */}
      <div className="h-12 border-b border-border bg-card/80 backdrop-blur flex items-center justify-between px-4 shrink-0 z-10">
        <div className="flex items-center gap-3">
          <Train className="w-4 h-4 text-primary" />
          <div className="font-mono text-xs tracking-widest text-primary">// NYCT SUBWAY · LIVE</div>
          <Badge variant="outline" data-testid="nyct-train-count">{trains.length} TRAINS LIVE</Badge>
          {allAlerts.length > 0 && (
            <Badge
              data-testid="alerts-badge"
              className="cursor-pointer bg-amber-500/20 text-amber-400 border-amber-500/50 hover:bg-amber-500/30"
              variant="outline"
              onClick={() => setShowAlerts(true)}
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> {allAlerts.length} ALERTS
            </Badge>
          )}
          <span className="font-mono text-[10px] text-muted-foreground">
            MTA GTFS-RT · {fetched ? new Date(fetched * 1000).toLocaleTimeString() : "—"}
          </span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {routes.map((r) => (
            <div key={r} className="flex items-center gap-1.5 shrink-0">
              <RouteBullet route={r} />
              <span className="font-mono text-xs">{routeCounts[r]}</span>
            </div>
          ))}
          <Button
            size="sm"
            variant={linesVisible ? "default" : "outline"}
            onClick={() => setLinesVisible((v) => !v)}
            data-testid="toggle-lines-btn"
            className="ml-2"
          >
            LINES
          </Button>
          <Button size="icon" variant="ghost" onClick={fetchTrains} data-testid="nyct-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative min-h-0">
        <MapContainer center={center} zoom={12} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer key={theme} url={tileUrl(theme)} attribution={tileAttribution()} maxZoom={TILE_MAX_ZOOM} />
          <FlyTo center={center} zoom={selected ? 14 : 12} />
          {follow && liveSelected && <FollowPan position={[liveSelected.lat, liveSelected.lon]} enabled={follow} />}

          {linesVisible && lines && (
            <GeoJSON
              key={`lines-${linesVisible}`}
              data={lines}
              style={linesStyle}
            />
          )}

          {/* Selected train trail */}
          {liveSelected && trails[liveSelected.trip_id]?.length > 1 && (
            <Polyline
              positions={trails[liveSelected.trip_id]}
              pathOptions={{
                color: routeColor(liveSelected.route),
                weight: 5,
                opacity: 0.9,
                dashArray: "8 6",
              }}
            />
          )}

          {liveTrains.map((t) => (
            <Marker
              key={t.trip_id}
              position={[t.lat, t.lon]}
              icon={trainIcon(t.route, t.direction, selected?.trip_id === t.trip_id)}
              eventHandlers={{ click: () => selectTrain(t) }}
              zIndexOffset={selected?.trip_id === t.trip_id ? 1000 : 0}
            >
              <Popup>
                <div className="font-mono text-xs space-y-0.5">
                  <div className="font-bold text-sm">{t.callsign}</div>
                  <div>Route {t.route} · {t.direction === "N" ? "Northbound" : t.direction === "S" ? "Southbound" : "—"}</div>
                  <div>{t.between}</div>
                  <div>To: {t.terminus || "—"}</div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Selected train detail card */}
        {selected && (
          <div className="absolute top-3 right-3 z-[400] w-96 bg-card/95 backdrop-blur border border-border rounded-md shadow-2xl" data-testid="train-detail-card">
            <div className="p-4 border-b border-border flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs text-primary tracking-widest">// TRAIN DETAIL · LIVE</div>
                <div className="flex items-center gap-2 mt-1">
                  <RouteBullet route={selected.route} />
                  <span className="font-mono text-lg font-bold">{selected.callsign}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {selected.direction === "N" ? "Northbound" : selected.direction === "S" ? "Southbound" : "—"}
                  {selected.terminus ? <> → <span className="text-foreground">{selected.terminus}</span></> : null}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant={follow ? "default" : "ghost"}
                  onClick={() => setFollow((v) => !v)}
                  title={follow ? "Stop following" : "Follow train"}
                  data-testid="follow-train-btn"
                >
                  <Target className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => { setSelected(null); setFollow(false); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <ScrollArea className="max-h-[60vh]">
              <div className="p-4 space-y-2 font-mono text-xs">
                <div className="border border-border p-2 rounded-sm">
                  <div className="text-muted-foreground text-[10px]">STATUS</div>
                  <div className="font-bold">{selected.status === "in_transit" ? "IN TRANSIT" : "APPROACHING STATION"}</div>
                </div>
                <div className="border border-border p-2 rounded-sm">
                  <div className="text-muted-foreground text-[10px]">POSITION</div>
                  <div className="font-bold">{selected.between}</div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="border border-border p-2 rounded-sm">
                    <div className="text-muted-foreground text-[10px]">NEXT STOP</div>
                    <div className="font-bold truncate">{selected.next_stop}</div>
                  </div>
                  <div className="border border-border p-2 rounded-sm">
                    <div className="text-muted-foreground text-[10px]">ETA</div>
                    <div className="font-bold">
                      {selected.next_arrival_min === 0 ? (
                        <span className="text-green-500">NOW</span>
                      ) : (
                        <>{selected.next_arrival_min} min</>
                      )}
                    </div>
                  </div>
                  <div className="border border-border p-2 rounded-sm">
                    <div className="text-muted-foreground text-[10px]">PREV STOP</div>
                    <div className="font-bold truncate">{selected.prev_stop || "—"}</div>
                  </div>
                  <div className="border border-border p-2 rounded-sm">
                    <div className="text-muted-foreground text-[10px]">STOPS REMAINING</div>
                    <div className="font-bold">{selected.remaining_stops}</div>
                  </div>
                  <div className="border border-border p-2 rounded-sm col-span-2">
                    <div className="text-muted-foreground text-[10px]">TRIP ID · STARTED</div>
                    <div className="font-bold truncate">{selected.trip_id}</div>
                    <div className="text-muted-foreground">{selected.start_date} @ {selected.start_time}</div>
                  </div>
                  <div className="border border-border p-2 rounded-sm col-span-2">
                    <div className="text-muted-foreground text-[10px]">COORDINATES · LIVE</div>
                    <div className="font-bold">{liveSelected.lat?.toFixed(5)}, {liveSelected.lon?.toFixed(5)}</div>
                  </div>
                </div>

                {/* Service alerts for this route */}
                {selectedAlerts.length > 0 && (
                  <div className="border border-amber-500/40 bg-amber-500/5 p-2 rounded-sm space-y-2 mt-3">
                    <div className="flex items-center gap-1.5 text-amber-400 text-[11px]">
                      <AlertTriangle className="w-3 h-3" />
                      {selectedAlerts.length} ACTIVE ALERT{selectedAlerts.length > 1 ? "S" : ""} ON THIS ROUTE
                    </div>
                    {selectedAlerts.slice(0, 3).map((a) => (
                      <div key={a.id} className="border-l-2 border-amber-500/60 pl-2 py-0.5">
                        <div className="text-[10px] text-amber-400 mb-0.5">{a.effect}</div>
                        <div className="font-sans text-xs leading-snug">{a.header}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
            <div className="px-4 pb-3 text-[10px] text-muted-foreground font-mono">
              Position interpolated from MTA GTFS-RT trip updates.
            </div>
          </div>
        )}

        {/* Alerts overlay */}
        {showAlerts && (
          <div className="absolute top-3 left-3 z-[400] w-[420px] max-h-[85vh] bg-card/95 backdrop-blur border border-amber-500/30 rounded-md shadow-2xl flex flex-col" data-testid="alerts-panel">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="font-mono text-xs tracking-widest text-amber-400 flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3" /> // MTA SERVICE ALERTS
                </div>
                <div className="text-xs text-muted-foreground mt-1">{allAlerts.length} active</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setShowAlerts(false)}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <ScrollArea className="flex-1">
              <div className="divide-y divide-border">
                {allAlerts.map((a) => (
                  <div key={a.id} className="p-3 hover:bg-accent/40">
                    <div className="flex items-center gap-1 mb-1 flex-wrap">
                      {a.routes.map((r) => <RouteBullet key={r} route={r} />)}
                      <Badge variant="outline" className="text-[10px] ml-1">{a.effect}</Badge>
                    </div>
                    <div className="text-sm font-medium leading-snug">{a.header}</div>
                    {a.description && (
                      <div className="text-[11px] text-muted-foreground mt-1 line-clamp-3">{a.description}</div>
                    )}
                  </div>
                ))}
                {!allAlerts.length && (
                  <div className="p-6 text-center text-muted-foreground font-mono text-xs">NO ACTIVE ALERTS</div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}
      </div>
    </div>
  );
}
